// Entry point for rawsql-ts package
export * from './parsers/SqlParser';
export * from './parsers/SqlTokenizer';
export * from './parsers/SelectQueryParser';
export { ParseAnalysisResult } from './parsers/SelectQueryParser';
export * from './parsers/InsertQueryParser';
export * from './parsers/UpdateQueryParser';
export * from './parsers/DeleteQueryParser';
export * from './parsers/WithClauseParser';
export * from './parsers/CreateTableParser';
export * from './parsers/MergeQueryParser';
export * from './parsers/CreateIndexParser';
export * from './parsers/DropTableParser';
export * from './parsers/DropIndexParser';
export * from './parsers/AlterTableParser';
export * from './parsers/DropConstraintParser';
export * from './parsers/CommentOnParser';
export * from './parsers/VacuumStatementParser';
export * from './parsers/ReindexStatementParser';
export * from './parsers/ClusterStatementParser';
export * from './parsers/CheckpointStatementParser';

export * from './types/Formatting';

export * from './models/BinarySelectQuery';
export * from './models/SelectQuery';
export * from './models/SqlComponent';
export * from './models/ValueComponent';
export * from './models/ValuesQuery';
export * from './models/Clause';
export * from './models/CTEError';
export * from './models/Lexeme';
export * from './models/FormattingLexeme';
export * from './models/InsertQuery';
export * from './models/UpdateQuery';
export * from './models/DeleteQuery';
export * from './models/CreateTableQuery';
export * from './models/MergeQuery';
export * from './models/DDLStatements';
export * from './models/TableDefinitionModel';

export * from './transformers/CTECollector';
export * from './transformers/CTENormalizer';
export * from './transformers/CTEDisabler';
export * from './transformers/CTEDependencyAnalyzer';
export * from './transformers/CTETableReferenceCollector';
export * from './transformers/CTEQueryDecomposer';
export type { CTERestorationResult } from './transformers/CTEQueryDecomposer';
export * from './transformers/NamedQueryDefinitionExtractor';
export * from './transformers/AstCommentAttachmentExtractor';
export * from './transformers/CTEComposer';
export * from './transformers/CTERenamer';
export * from './transformers/AliasRenamer';
export * from './transformers/SmartRenamer';
export * from './formatters/OriginalFormatRestorer';
export * from './transformers/SqlIdentifierRenamer';
export type { ScopeRange } from './transformers/SqlIdentifierRenamer';
export * from './transformers/ColumnReferenceCollector';
export * from './transformers/ClauseScopedColumnReferenceCollector';
export * from './transformers/Formatter';
export * from './transformers/SqlFormatter';
export * from './transformers/QueryBuilder'; // old name:QueryConverter
export * from './transformers/InsertQuerySelectValuesConverter';
export * from './transformers/InsertResultSelectConverter';
export type { MissingFixtureStrategy } from './transformers/InsertResultSelectConverter';
export * from './transformers/UpdateResultSelectConverter';
export * from './transformers/DeleteResultSelectConverter';
export * from './transformers/MergeResultSelectConverter';
export * from './transformers/SelectResultSelectConverter';
export * from './transformers/SimulatedSelectConverter';
export * from './transformers/DDLToFixtureConverter';
export * from './transformers/DDLGeneralizer';
export * from './transformers/DDLDiffGenerator';
export * from './transformers/SelectBodyExtractor';
export * from './transformers/SelectOutputCollector';
export * from './transformers/WildcardColumnInferenceCollector';
export * from './transformers/SelectValueCollector';
export * from './transformers/SelectableColumnCollector';
export { DuplicateDetectionMode } from './transformers/SelectableColumnCollector';
export * from './transformers/FilterableItemCollector';
export { FixtureCteBuilder, FixtureTableDefinition, FixtureColumnDefinition } from './transformers/FixtureCteBuilder';
export * from './transformers/DynamicQueryBuilder';
export * from './transformers/SSSQLFilterBuilder';
export type {
    FormattableSqlComponent,
    SqlComponentFormatter,
    SqlComponentFormatOptions
} from './transformers/SqlComponentFormatter';
export * from './transformers/ConditionOptimization';
export * from './transformers/ParameterConditionPlacementOptimizer';
export * from './transformers/PredicateReachabilityAnalyzer';
export * from './transformers/StaticPredicatePlacementOptimizer';
export * from './transformers/PruneOptionalConditionBranches';
export {
    SchemaInfo,
    optimizeUnusedLeftJoins,
    optimizeUnusedLeftJoinsToFixedPoint,
    optimizeUnusedCtes,
    optimizeUnusedCtesToFixedPoint
} from './transformers/OptimizeUnusedLeftJoins';
export * from './transformers/TableColumnResolver';
export * from './transformers/TableSourceCollector';
export * from './transformers/UpstreamSelectQueryFinder';

export * from './transformers/SchemaCollector';
export { TableSchema, SchemaAnalysisResult } from './transformers/SchemaCollector';
export * from './transformers/FilterableItemCollector';
export { FilterableItem, FilterableItemCollectorOptions } from './transformers/FilterableItemCollector';
export * from './transformers/QueryFlowDiagramGenerator';
export * from './transformers/SqlParamInjector';
export * from './transformers/SqlSortInjector';
export * from './transformers/SqlPaginationInjector';
export * from './transformers/DynamicQueryBuilder';

export * from './utils/ParameterHelper';
export * from './transformers/SqlParameterBinder';
export * from './utils/SqlSchemaValidator';
export * from './utils/SchemaManager';
export * from './utils/RelationGraph';
export * from './utils/CommentEditor';
export * from './utils/LexemeCursor';
export type { LineColumn } from './utils/LexemeCursor';
export * from './utils/CTERegionDetector';
export type { CTERegion, CursorPositionInfo } from './utils/CTERegionDetector';

// Position-aware parsing and IntelliSense support
export * from './utils/CursorContextAnalyzer';
export type { IntelliSenseContext } from './utils/CursorContextAnalyzer';
export * from './utils/ScopeResolver';
export type {
    ScopeInfo,
    AvailableTable,
    AvailableCTE,
    AvailableColumn
} from './utils/ScopeResolver';
export * from './utils/PositionAwareParser';
export type {
    ParseToPositionOptions,
    PositionParseResult
} from './utils/PositionAwareParser';
export * from './utils/MultiQuerySplitter';
export type { QueryInfo, QueryCollection } from './utils/MultiQuerySplitter';

export { normalizeTableName, tableNameVariants } from './utils/TableNameUtils';

// Generic fixtures for driver/test-kit layers
export * from './types/GenericFixture';

// Convenience functions for IntelliSense integration
export {
    /** Parse SQL up to cursor position with error recovery */
    parseToPosition,
    /** Analyze cursor context for IntelliSense */
    getCursorContext,
    /** Resolve scope information at cursor position */
    resolveScope,
    /** Split multi-query SQL text into individual queries */
    splitQueries,
    /** Get IntelliSense information for multi-query context */
    getIntelliSenseInfo,
    /** Get completion suggestions based on cursor context */
    getCompletionSuggestions
} from './utils/IntelliSenseApi';


// Add more exports here if you want to expose additional public API
